import { useLangStore } from "../stores";
import { AccessTokenBody } from "../types/auth";
import { ClassGroupInfo } from "../types/class";
import { dateTimeLocales } from "../types/lang";
import { Session, SessionPoint } from "../types/session";

export function decodeJwt(token: string | null): AccessTokenBody | null {
    if (!token) return null;

    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

export function formatTimestampToArr(timestamp: number | null | undefined, withSeconds?: boolean): string[] {
    if (!timestamp) return [];

    var a = new Date(timestamp * 1000);
    var months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    var year = a.getFullYear();
    var month = months[a.getMonth()];
    var date = a.getDate();
    var hour = a.getHours();
    var min = a.getMinutes();
    var sec = a.getSeconds();
    const addZero = (num: number) => {
        return (num < 10) ? '0' + num : num.toString();
    }
    const dateArr = [addZero(date), addZero(month), addZero(year)]
    const timeArr = [addZero(hour), addZero(min)]
    if (withSeconds) {
        timeArr.push(addZero(sec))
    }

    return [dateArr.join('/'), timeArr.join(':')]
}

export function parseTimestampToDateWithMonthName(timestamp: number | null | undefined): string {
    if (!timestamp) return ''
    const date = new Date(timestamp * 1000);
    const langStore = useLangStore();
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
    };
    return `${date.toLocaleDateString(dateTimeLocales[langStore.locale], options)}`
}

export function formatTimestampToDate(timestamp: number | null | undefined): string {
    const [dateFormat] = formatTimestampToArr(timestamp)
    return dateFormat
}

export function formatTimestampToTime(timestamp: number | null | undefined, withSeconds?: boolean): string {
    const [_, timeFormat] = formatTimestampToArr(timestamp, withSeconds)
    return timeFormat
}

export function formatTimestampToDateTime(timestamp: number | null | undefined, withSeconds?: boolean): string {
    const [dateFormat, timeFormat] = formatTimestampToArr(timestamp, withSeconds)
    if (timeFormat === '00:00:00' || timeFormat === '00:00') {
        return dateFormat
    }

    return `${dateFormat} - ${timeFormat}`
}

export function formatTimeStringNoSeconds(timeString: string | null | undefined): string {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':')
    return `${hours}:${minutes}`
}

export function calculateTotalPoint(point: SessionPoint | undefined | null): number {
    if (!point) return 0;

    const totalPoint = Object.values(Session).reduce((totalPoint, session) => {
        return totalPoint += point[session] || 0
    }, 0)

    return totalPoint
}

export function calculateGroupTotalPoint(group: ClassGroupInfo): number {
    const groupPoint = calculateTotalPoint(group.point)
    const studentPoints = group.students?.reduce((total, student) => {
        return total += calculateTotalPoint(student.point)
    }, 0) || 0

    return groupPoint + studentPoints
}

export function timeStringToMilliseconds(timeString: string): number {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        return 0
    }

    // Convert to milliseconds
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
}

export function ordinalSuffixOf(i: number | undefined): string {
    if (!i) return '--';
    let j = i % 10,
        k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
}

export function stripHtmlTags(content: string): string {
    return content.replace(/<\/?[^>]+(>|$)/g, "")
}

export function decodeHtmlEntities(str: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.documentElement.textContent || '';
}

export function isValidUrl(url: string): boolean {
    try {
        const parsedUrl = new URL(url)
        return Boolean(parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:")
    } catch {
        return false
    }
}

export function containsEmptyString(arr: string[]): boolean {
    return arr.some(item => item === '');
}

export function formatSecondsToMinuteSeconds(seconds: number, format: 'mm:ss' | 'mmms' = 'mm:ss'): string {
    if (!seconds) return format === 'mm:ss' ? '00:00' : '00m00s';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return format === 'mm:ss'
        ? `${formattedMinutes}:${formattedSeconds}`
        : `${formattedMinutes}m${formattedSeconds}s`;
}

export function debounce<T extends (...args: any[]) => any>(fn: T, delay = 500): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>
    return (...args: Parameters<T>) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            fn(...args)
        }, delay)
    }
}

export function roundScore(score: number): number {
    return Math.round(score * 2) / 2
}